"use client";
import { publicErrorMessage } from "@ocr-monorepo/shared";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@ocr-monorepo/ui/components/dialog";
import type { PdfToSqliteDemoDocumentResponse } from "@/lib/pdf-to-sqlite-demo";
import { captureGrowthEvent } from "@/lib/analytics";

async function fetchDocument(invoiceNumber: string) {
  const response = await fetch(
    `/api/pdf-to-sqlite-demo/documents/${encodeURIComponent(invoiceNumber)}`,
  );
  const body = (await response.json()) as
    | PdfToSqliteDemoDocumentResponse
    | { error?: string };
  if (!response.ok || !("invoice" in body)) {
    throw new Error(
      "error" in body && body.error
        ? body.error
        : "The source document could not be loaded.",
    );
  }
  return body;
}

const formatMoney = (value: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(value);

export function PdfToSqliteDemoDocument({
  invoiceNumber,
  onOpenChange,
}: {
  invoiceNumber: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const documentQuery = useQuery({
    queryKey: ["pdf-to-sqlite-demo", "document", invoiceNumber],
    queryFn: () => fetchDocument(invoiceNumber!),
    enabled: Boolean(invoiceNumber),
    staleTime: 60 * 60 * 1_000,
  });

  return (
    <Dialog open={Boolean(invoiceNumber)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto p-0">
        <DialogHeader className="border-b border-border px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="size-4 text-primary" /> Associated source
            document
          </DialogTitle>
          <DialogDescription>
            This fake PDF template is populated from the same D1 record returned
            by the live query.
          </DialogDescription>
        </DialogHeader>

        {documentQuery.isPending ? (
          <div className="flex min-h-96 items-center justify-center p-8">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        ) : documentQuery.isError ? (
          <div className="flex min-h-96 items-center justify-center p-8 text-center">
            <div>
              <AlertTriangle className="mx-auto size-5 text-destructive" />
              <p className="mt-3 text-sm font-semibold">Document unavailable</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {publicErrorMessage("system_error")}
              </p>
            </div>
          </div>
        ) : documentQuery.data ? (
          <div className="bg-muted/30 p-5 sm:p-8">
            <article className="mx-auto rounded-lg border border-neutral-300 bg-[#fffdf8] p-6 text-neutral-900 shadow-sm sm:p-8">
              <header className="flex items-start justify-between gap-6 border-b border-neutral-300 pb-5">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[.24em] text-neutral-500">
                    Shipping invoice · demonstration template
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                    {documentQuery.data.invoice.invoice_number}
                  </h2>
                  <p className="mt-1 font-mono text-[10px] text-neutral-500">
                    {documentQuery.data.invoice.source_document}
                  </p>
                </div>
                <div className="text-right font-mono text-[9px] uppercase leading-5 text-neutral-500">
                  <p>{documentQuery.data.invoice.status}</p>
                  <p>Page 1 of 1</p>
                </div>
              </header>

              <dl className="grid grid-cols-2 gap-x-8 gap-y-4 py-6 text-[11px] sm:grid-cols-3">
                {[
                  ["Carrier", documentQuery.data.invoice.carrier],
                  ["Tracking", documentQuery.data.invoice.tracking_number],
                  ["Reference", documentQuery.data.invoice.shipment_reference],
                  ["Shipper", documentQuery.data.invoice.shipper_name],
                  ["Consignee", documentQuery.data.invoice.consignee_name],
                  [
                    "Route",
                    `${documentQuery.data.invoice.origin} → ${documentQuery.data.invoice.destination}`,
                  ],
                  ["Issued", documentQuery.data.invoice.issue_date],
                  ["Expected", documentQuery.data.invoice.expected_delivery],
                  ["Currency", documentQuery.data.invoice.currency],
                ].map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="font-mono text-[8px] uppercase tracking-wider text-neutral-500">
                      {label}
                    </dt>
                    <dd className="mt-1 truncate font-medium">{value}</dd>
                  </div>
                ))}
              </dl>

              <section className="border-t border-neutral-300 pt-5">
                <p className="font-mono text-[8px] uppercase tracking-wider text-neutral-500">
                  Tracking history · {documentQuery.data.trackingEvents.length}{" "}
                  related database rows
                </p>
                <div className="mt-3 overflow-hidden rounded border border-neutral-300">
                  <div className="grid grid-cols-[2rem_8rem_1fr_1fr] bg-neutral-100 px-3 py-2 font-mono text-[8px] uppercase tracking-wider text-neutral-500">
                    <span>#</span>
                    <span>Time</span>
                    <span>Status</span>
                    <span>Location</span>
                  </div>
                  {documentQuery.data.trackingEvents.map((event) => (
                    <div
                      key={event.id}
                      className="grid grid-cols-[2rem_8rem_1fr_1fr] border-t border-neutral-200 px-3 py-2 text-[10px]"
                    >
                      <span>{event.checkpoint_sequence}</span>
                      <span>{event.event_time}</span>
                      <span className="font-medium">{event.event_status}</span>
                      <span>{event.location}</span>
                    </div>
                  ))}
                </div>
              </section>

              <dl className="ml-auto mt-6 w-full max-w-xs space-y-2 border-t border-neutral-300 pt-4 text-[11px]">
                {[
                  ["Subtotal", documentQuery.data.invoice.subtotal],
                  ["Freight", documentQuery.data.invoice.freight_charge],
                  ["Insurance", documentQuery.data.invoice.insurance_charge],
                  ["Tax", documentQuery.data.invoice.tax],
                  ["Total", documentQuery.data.invoice.total_amount],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-5">
                    <dt className="text-neutral-500">{label}</dt>
                    <dd className="font-mono font-semibold">
                      {formatMoney(
                        Number(value),
                        documentQuery.data.invoice.currency,
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </article>

            <p className="mt-4 text-center text-xs text-muted-foreground">
              Want to upload and query your own documents?{" "}
              <Link
                href="/getting-started/create-account?source=pdf-to-sqlite-demo-document"
                onClick={() =>
                  captureGrowthEvent("sqlite_demo_signup_clicked", {
                    source: "source_document",
                  })
                }
                className="font-semibold text-primary hover:underline"
              >
                Create a free workspace
              </Link>
              .
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
